// ===========================================
// Add a catalogue product as a line — inside the amend dialog (2026-09-28)
// ===========================================
// The draft invoice page adds a product through its own endpoint (DRAFT only).
// An amendment sends its WHOLE line list on save, so here a product simply
// becomes one more line in the dialog — carrying its `productId`, so it stays a
// product line (reporting, stock) rather than free text. The server re-checks
// the product belongs to the school, is active, and — for a new or increased
// quantity — that stock covers it.

import { useState } from 'react';
import { Autocomplete, Box, Button, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useProducts } from '../../hooks/useProducts';
import type { Product } from '../../api/product.api';
import type { CreateInvoiceItemInput } from '../../api/invoices.api';

/** Mirrors the add-product endpoint's bound (int, 1..100). */
const MAX_QUANTITY = 100;

interface AmendProductPickerProps {
  /** `productCategoryId`: the product's category now, for the dialog's coverage estimate (#625). The server stamps its own. */
  onAdd: (line: CreateInvoiceItemInput & { productCategoryId: string | null }) => void;
}

export default function AmendProductPicker({ onAdd }: AmendProductPickerProps) {
  const { data: products, isLoading, isError } = useProducts({ isActive: true });
  const [product, setProduct] = useState<Product | null>(null);
  const [quantity, setQuantity] = useState('1');
  const [unitPrice, setUnitPrice] = useState('');

  const parsedQuantity = parseInt(quantity, 10);
  const quantityIsValid =
    /^\d+$/.test(quantity.trim()) && parsedQuantity >= 1 && parsedQuantity <= MAX_QUANTITY;
  const parsedPrice = parseFloat(unitPrice);
  // An invoice line must be priced above $0 (the dialog blocks a $0 line on save).
  const priceIsValid = Number.isFinite(parsedPrice) && parsedPrice > 0;
  const outOfStock =
    !!product && product.trackInventory && product.quantity !== null && quantityIsValid && parsedQuantity > product.quantity;

  const reset = () => {
    setProduct(null);
    setQuantity('1');
    setUnitPrice('');
  };

  const handleAdd = () => {
    if (!product || !quantityIsValid || !priceIsValid || outOfStock) return;
    onAdd({
      description: product.name,
      quantity: parsedQuantity,
      unitPrice: parsedPrice,
      productId: product.id,
      productCategoryId: product.categoryId ?? null,
    });
    reset();
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Add a product
      </Typography>
      {/* The product list needs operations_pricing:read, which an invoice writer may
          not have — say so rather than show an empty list (code review L1, 2026-10-02). */}
      {isError && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
          The product list couldn&apos;t be loaded (it needs access to pricing). You can add the item
          as an ordinary line with Add Item instead.
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <Autocomplete
          sx={{ flex: '1 1 220px' }}
          size="small"
          options={products ?? []}
          loading={isLoading}
          value={product}
          onChange={(_e, next) => {
            setProduct(next);
            // Selecting a product seeds its catalogue price; the admin may override it.
            setUnitPrice(next ? String(Number(next.price)) : '');
          }}
          getOptionLabel={(p) => p.name}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderInput={(params) => <TextField {...params} label="Product" />}
        />
        <TextField
          sx={{ width: 90 }}
          size="small"
          label="Qty"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          error={quantity.trim() !== '' && !quantityIsValid}
        />
        <TextField
          sx={{ width: 120 }}
          size="small"
          label="Price ($)"
          value={unitPrice}
          onChange={(e) => setUnitPrice(e.target.value)}
          error={unitPrice.trim() !== '' && !priceIsValid}
        />
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!product || !quantityIsValid || !priceIsValid || outOfStock}
        >
          {/* "Add product", not "Add": the draft edit also shows the week picker's
              "Add" (2026-10-02), and two identical buttons read as one. */}
          Add product
        </Button>
      </Box>
      {outOfStock && product && (
        <Typography variant="caption" color="error">
          Only {product.quantity} of &quot;{product.name}&quot; in stock.
        </Typography>
      )}
    </Box>
  );
}
